import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Code,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type {
  AdminOrderSideEffectJob,
  OrderSideEffectJobStatus,
} from '@/types'
import { formatDateTime } from '@/utils/formatUtils'

type OrderSideEffectJobsTableProps = {
  jobs: AdminOrderSideEffectJob[]
  isLoading?: boolean
  error?: unknown
  observedAt?: string | null
  showOrderId?: boolean
  emptyMessage?: string
}

export const getOrderSideEffectJobStatusColor = (
  status: OrderSideEffectJobStatus
): string => {
  switch (status) {
    case 'completed':
      return 'green'
    case 'failed':
      return 'red'
    case 'processing':
      return 'blue'
    case 'retry_scheduled':
      return 'orange'
    case 'cancelled':
      return 'gray'
    case 'pending':
    default:
      return 'yellow'
  }
}

const formatTimestamp = (value: string | null | undefined, fallback: string) => {
  return value ? formatDateTime(value) : fallback
}

export function OrderSideEffectJobsTable({
  jobs,
  isLoading = false,
  error,
  observedAt,
  showOrderId = true,
  emptyMessage,
}: OrderSideEffectJobsTableProps) {
  const { t } = useTranslation()

  if (isLoading && jobs.length === 0) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="md" />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error instanceof Error ? error.message : t('common.operationFailed')}
      </Alert>
    )
  }

  return (
    <Box>
      {jobs.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {emptyMessage ?? t('monitoring.sideEffectJobs.empty')}
        </Alert>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('monitoring.sideEffectJobs.columns.job')}</Th>
                {showOrderId && (
                  <Th>{t('monitoring.sideEffectJobs.columns.order')}</Th>
                )}
                <Th>{t('monitoring.sideEffectJobs.columns.type')}</Th>
                <Th>{t('monitoring.sideEffectJobs.columns.status')}</Th>
                <Th>{t('monitoring.sideEffectJobs.columns.attempts')}</Th>
                <Th>{t('monitoring.sideEffectJobs.columns.nextRun')}</Th>
                <Th>{t('monitoring.sideEffectJobs.columns.lockedUntil')}</Th>
                <Th>{t('monitoring.sideEffectJobs.columns.error')}</Th>
                <Th>{t('monitoring.sideEffectJobs.columns.updated')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobs.map(job => (
                <Tr key={job.id}>
                  <Td>
                    <Code fontSize="xs">{job.id}</Code>
                  </Td>
                  {showOrderId && (
                    <Td>
                      <Code fontSize="xs">{job.orderId}</Code>
                    </Td>
                  )}
                  <Td>
                    <Text fontSize="xs">
                      {t(`monitoring.sideEffectJobs.jobTypes.${job.jobType}`, {
                        defaultValue: job.jobType,
                      })}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getOrderSideEffectJobStatusColor(job.status)}>
                      {t(`monitoring.sideEffectJobs.statuses.${job.status}`, {
                        defaultValue: job.status,
                      })}
                    </Badge>
                  </Td>
                  <Td>{job.attempts} / {job.maxAttempts}</Td>
                  <Td fontSize="xs">
                    {formatTimestamp(job.nextRunAt, t('common.na'))}
                  </Td>
                  <Td fontSize="xs">
                    {formatTimestamp(job.lockedUntil, t('common.na'))}
                  </Td>
                  <Td fontSize="xs">
                    {job.lastErrorCategory
                      ? t(
                        `monitoring.sideEffectJobs.errorCategories.${job.lastErrorCategory}`,
                        { defaultValue: job.lastErrorCategory }
                      )
                      : t('common.na')}
                  </Td>
                  <Td fontSize="xs">
                    {formatTimestamp(job.updatedAt, t('common.na'))}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      {observedAt && (
        <Text mt={2} fontSize="xs" color="gray.500" textAlign="right">
          {t('monitoring.sideEffectJobs.observedAt', {
            time: formatDateTime(observedAt),
          })}
        </Text>
      )}
    </Box>
  )
}
